/**
 * B-24b WS-3 — Per-customer drill overlay for the Operator Console.
 *
 * Triggered by row-click on AttentionPanel, AccountHealthPanel, or the
 * UsageTrendPanel. Carries:
 *   - health + calibration health
 *   - model-override status + escalate-inline action
 *   - consumed / available credits + monthly burn (READ-ONLY)
 *   - contract value + renewal date (from tenant_contract, NOT Stripe)
 *
 * Sources:
 *   - /admin/dashboard/tenant/:id/detail (existing — model overrides, credits,
 *     subscription)
 *   - /admin/sa-dashboard/calibration-health (cached from parent, passed as prop)
 *   - /admin/sa-dashboard/account-health (cached from parent, passed as prop)
 *
 * The overlay is intentionally READ-ONLY from the credit path (IV-K1).
 * Escalate button is a stub (WS-2/model override write path is out of scope
 * for this UI batch; it navigates to the existing ModelOverrides page).
 *
 * TWO-ZONE: everything here is tenant-scoped (nameable). No platform-aggregate
 * data from platform_rating_signals is shown here (IV-Z5 / IV-Z6).
 */
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { fetchTenantDetail } from "@/lib/dashboardApi";
import type { TenantDetailOverlayResponse } from "@/lib/dashboardApi";
import type {
  CalibrationHealthTenant,
  AccountHealthTenant,
} from "@/lib/saDashboardApi";

interface Props {
  tenantId: string | null;
  tenantName: string | null;
  calibrationData: CalibrationHealthTenant | undefined;
  accountData: AccountHealthTenant | undefined;
  onClose: () => void;
}

function fmtCredits(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function fmtDollars(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString()}`;
}

function fmtContractValue(cents: number | null): string {
  if (cents === null) return "no contract on file";
  if (cents === 0) return "$0";
  return fmtDollars(cents);
}

function StatRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-border-default last:border-0">
      <span className="shrink-0 text-2xs font-bold uppercase tracking-[0.12em] text-text-tertiary">
        {label}
      </span>
      <span className="text-right text-xs text-text-primary">{children}</span>
    </div>
  );
}

function DrillBody({
  tenantId,
  tenantName,
  calibration,
  account,
}: {
  tenantId: string;
  tenantName: string;
  calibration: CalibrationHealthTenant | undefined;
  account: AccountHealthTenant | undefined;
}) {
  const navigate = useNavigate();
  const q = useQuery<TenantDetailOverlayResponse, Error>({
    queryKey: ["admin", "dashboard", "tenant", tenantId, "detail"],
    queryFn: () => fetchTenantDetail(tenantId),
    staleTime: 60_000,
  });

  const detail = q.data;
  const modelOverrides = detail?.model_overrides ?? [];
  const creditBalance = detail?.credit_balance ?? null;
  const monthlyCostCents = detail?.estimated_cost_cents_30d ?? null;

  // Derive health from calibration data
  const passRate = calibration?.bias_pass_rate ?? null;
  const passRateClass =
    passRate === null
      ? "text-text-tertiary italic"
      : passRate >= 98
        ? "text-brand-positive font-semibold"
        : passRate >= 94
          ? "text-brand-warn font-semibold"
          : "text-brand-negative font-semibold";

  return (
    <div className="font-sans">
      {/* Identity */}
      <div className="border-b border-border-default pb-3">
        <p className="text-2xs font-bold uppercase tracking-[0.14em] text-accent-secondary">
          Tenant drill-down
        </p>
        <h3 className="mt-1 text-lg font-bold tracking-tight text-text-emphasis">
          {tenantName}
        </h3>
        {account && (
          <p className="mt-0.5 text-xs text-text-tertiary capitalize">
            {account.status}
            {account.contract_value_cents !== null
              ? " · " + fmtContractValue(account.contract_value_cents) + "/yr"
              : ""}
          </p>
        )}
      </div>

      {q.isLoading && (
        <div className="mt-4 space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-6 animate-pulse rounded bg-surface-canvas" />
          ))}
        </div>
      )}
      {q.isError && (
        <p className="mt-4 text-xs text-brand-negative">
          Couldn't load tenant detail.
        </p>
      )}

      {/* Tenant-scoped signals */}
      {detail && (
        <div className="mt-3 divide-y divide-border-default">
          <StatRow label="Calibration pass rate">
            <span className={passRateClass}>
              {passRate === null ? "building baseline" : `${passRate.toFixed(1)}%`}
            </span>
            {calibration && (
              <span className="ml-2 text-2xs text-text-tertiary">
                {calibration.calibrated ? "earned" : "not yet calibrated"}
              </span>
            )}
          </StatRow>

          <StatRow label="Guardrails version">
            <span className="font-mono text-text-primary">
              {calibration?.guardrails_version ?? "—"}
            </span>
            {calibration?.drift && (
              <span className="ml-2 text-2xs font-semibold text-brand-warn">
                drift — update needed
              </span>
            )}
          </StatRow>

          <StatRow label="Model override">
            {modelOverrides.length === 0 ? (
              <span className="text-text-tertiary italic">none set</span>
            ) : (
              <div className="space-y-0.5">
                {modelOverrides.map((o) => (
                  <div key={o.action_type} className="text-right">
                    <span className="font-mono text-text-primary uppercase tracking-wide">
                      {o.action_type}
                    </span>
                    <span className="ml-2 text-text-secondary">→ {o.model_name}</span>
                  </div>
                ))}
              </div>
            )}
          </StatRow>

          <StatRow label="Credits available">
            <span className="font-mono tabular-nums text-text-primary">
              {creditBalance !== null ? fmtCredits(creditBalance) : "—"}
            </span>
          </StatRow>

          <StatRow label="Est. monthly burn">
            <span className="font-mono tabular-nums text-text-primary">
              {monthlyCostCents !== null && monthlyCostCents > 0
                ? fmtDollars(monthlyCostCents)
                : "—"}
            </span>
          </StatRow>

          {account && account.contract_value_cents !== null && (
            <StatRow label="Contract value">
              <span className="font-mono tabular-nums text-text-primary">
                {fmtContractValue(account.contract_value_cents)}
              </span>
            </StatRow>
          )}

          {account && account.renewal_date && (
            <StatRow label="Renewal date">
              <span className="font-mono tabular-nums text-text-primary">
                {account.renewal_date}
              </span>
              {account.churn_risk !== "low" && account.churn_risk !== "unknown" && (
                <span
                  className={
                    "ml-2 text-2xs font-semibold capitalize " +
                    (account.churn_risk === "high"
                      ? "text-brand-negative"
                      : "text-brand-warn")
                  }
                >
                  {account.churn_risk} churn risk
                </span>
              )}
            </StatRow>
          )}
        </div>
      )}

      {/* Inline actions */}
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => navigate(`/tenants/${tenantId}/model-overrides`)}
          className="rounded-md bg-brand-grad px-3 py-1.5 text-2xs font-bold uppercase tracking-[0.12em] text-text-on-brand shadow-sm hover:brightness-110"
        >
          Manage model override
        </button>
        <button
          type="button"
          onClick={() => navigate(`/tenants/${tenantId}`)}
          className="rounded-md border border-border-strong px-3 py-1.5 text-2xs font-bold uppercase tracking-[0.12em] text-text-secondary hover:border-accent-secondary hover:text-text-primary"
        >
          Open full tenant
        </button>
      </div>
    </div>
  );
}

export function OperatorDrillOverlay({
  tenantId,
  tenantName,
  calibrationData,
  accountData,
  onClose,
}: Props) {
  if (!tenantId || !tenantName) return null;
  return (
    <div
      role="dialog"
      aria-label="Tenant detail"
      aria-modal="true"
      className="fixed inset-0 z-50 flex justify-end"
    >
      <button
        type="button"
        aria-label="Close drill-down"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
      />
      <aside className="relative h-full w-full max-w-md overflow-y-auto border-l border-border-strong bg-surface-card p-5 shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 rounded p-1 text-text-tertiary hover:bg-surface-card-hover hover:text-text-primary"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M4 4l8 8M12 4l-8 8"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <DrillBody
          tenantId={tenantId}
          tenantName={tenantName}
          calibration={calibrationData}
          account={accountData}
        />
      </aside>
    </div>
  );
}
